import type { Vec3 } from './types';

/** シェーダの光源テクスチャに載せる上限。品質プリセットもこれを超えない。 */
export const RESTIR_LIGHT_LIST_CAPACITY = 256;
/** 枠の境界で点滅しないよう、前回選ばれた光源を少し優遇する。 */
export const POINT_LIGHT_HYSTERESIS = 2.5;

export interface PointLightSource {
  key: string;
  x: number;
  y: number;
  z: number;
  /** 光源を内包する形状の半径。SDF 可視レイが発光体自身を遮蔽物として拾うのを防ぐ。 */
  sourceRadius: number;
  color: number;
  intensity: number;
  range: number;
}

export interface RankedPointLight {
  source: PointLightSource;
  distance: number;
}

/** reservoir の数値 index を前フレームのまま再利用できる光源配列か。 */
export function pointLightHistoryCompatible(
  previous: readonly PointLightSource[],
  current: readonly PointLightSource[]
): boolean {
  if (previous.length !== current.length) return false;
  return previous.every((old, index) => {
    const next = current[index];
    return (
      old.key === next.key &&
      old.x === next.x &&
      old.y === next.y &&
      old.z === next.z &&
      old.sourceRadius === next.sourceRadius &&
      old.color === next.color &&
      old.intensity === next.intensity &&
      old.range === next.range
    );
  });
}

/**
 * 前フレームのスロット番号を今フレームのスロットへ付ける。
 * 消えた光源は -1。履歴を捨てずに reservoir の index だけ差し替える。
 */
export function remapPointLightIndices(
  previous: readonly PointLightSource[],
  current: readonly PointLightSource[]
): Int32Array {
  const remap = new Int32Array(RESTIR_LIGHT_LIST_CAPACITY);
  remap.fill(-1);
  if (previous.length === 0 || current.length === 0) return remap;
  const currentIndex = new Map<string, number>();
  for (let i = 0; i < current.length; i++) currentIndex.set(current[i].key, i);
  const count = Math.min(previous.length, RESTIR_LIGHT_LIST_CAPACITY);
  for (let i = 0; i < count; i++) {
    const next = currentIndex.get(previous[i].key);
    remap[i] = next === undefined ? -1 : next;
  }
  return remap;
}

function distanceTo(source: PointLightSource, camera: Vec3): number {
  const dx = source.x - camera.x;
  const dy = source.y - camera.y;
  const dz = source.z - camera.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * 視錐台に残った点光源から、テクスチャに載せる最大数だけ選ぶ。
 * far が指定されていれば、カメラからその距離まで volume が届かない光源は捨てる。
 * 順位はカメラへの距離。前回選ばれたものは hysteresis 分だけ短く見て点滅しない。
 */
export function selectNearbyPointLights(
  sources: readonly PointLightSource[],
  camera: Vec3,
  maxLights: number,
  options?: {
    far?: number;
    hysteresis?: number;
    previousKeys?: ReadonlySet<string>;
  }
): RankedPointLight[] {
  if (maxLights <= 0 || sources.length === 0) return [];
  const far = options?.far;
  const hysteresis = options?.hysteresis ?? POINT_LIGHT_HYSTERESIS;
  const previousKeys = options?.previousKeys;
  const ranked: RankedPointLight[] = [];

  for (const source of sources) {
    if (far !== undefined && distanceTo(source, camera) - source.range > far) continue;
    ranked.push({ source, distance: distanceTo(source, camera) });
  }

  ranked.sort((a, b) => {
    const aScore = previousKeys?.has(a.source.key) ? a.distance - hysteresis : a.distance;
    const bScore = previousKeys?.has(b.source.key) ? b.distance - hysteresis : b.distance;
    if (aScore !== bScore) return aScore - bScore;
    return a.source.key < b.source.key ? -1 : a.source.key > b.source.key ? 1 : 0;
  });

  return ranked.slice(0, maxLights);
}

/**
 * 選ばれた光源をテクスチャスロットへ載せる。残った光源は前回の index を保ち、
 * 外れたスロットだけ末尾と入れ替える。新規は末尾へ足す。
 */
export function packPointLightsStable(
  selected: readonly PointLightSource[],
  previous: readonly PointLightSource[],
  maxLights: number
): PointLightSource[] {
  const capacity = Math.max(0, Math.min(maxLights, RESTIR_LIGHT_LIST_CAPACITY));
  if (capacity <= 0 || selected.length === 0) return [];

  const selectedByKey = new Map<string, PointLightSource>();
  for (const source of selected) selectedByKey.set(source.key, source);

  const packed: PointLightSource[] = previous.map((source) => selectedByKey.get(source.key) ?? source);
  for (let i = packed.length - 1; i >= 0; i--) {
    if (selectedByKey.has(packed[i].key)) continue;
    const last = packed.pop();
    if (last && i < packed.length) packed[i] = last;
  }

  const used = new Set(packed.map((source) => source.key));
  for (const source of selected) {
    if (packed.length >= capacity) break;
    if (used.has(source.key)) continue;
    packed.push(source);
    used.add(source.key);
  }

  if (packed.length > capacity) packed.length = capacity;
  return packed;
}
